

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useReducedMotion } from 'framer-motion';
import { canVibrate, haptic, type HapticPattern } from '@/lib/haptics';

export interface UseHapticsResult {
  /** Fire a named haptic pattern (no-ops when unavailable/disabled). */
  fire: (p: HapticPattern) => void;
  /** True when the device/browser can vibrate (false during SSR/hydration). */
  available: boolean;
}

/**
 * Memo-stable component wrapper around src/lib/haptics.ts.
 *
 * Adds framer-motion's useReducedMotion() as an extra reactive gate on top of
 * the lib's own guards (enabled flag, capability, media query, coalescing).
 * `available` starts false and settles after mount so SSR markup never
 * disagrees with the client.
 */
export function useHaptics(): UseHapticsResult {
  const reducedMotion = useReducedMotion();
  const [available, setAvailable] = useState(false);

  useEffect(() => {
    setAvailable(canVibrate());
  }, []);

  const fire = useCallback(
    (p: HapticPattern) => {
      if (reducedMotion) return;
      haptic(p);
    },
    [reducedMotion]
  );

  return useMemo(() => ({ fire, available }), [fire, available]);
}
